import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  LineChannelCreate,
  LineIntakeStatus,
  LineSenderAllowlistCreate,
} from '@dcc-erp/shared';

import { api, apiJson } from '../api';

// Channel rows are returned with secrets redacted server-side (see
// line-intake.service redactChannel) — the web layer never sees channelSecret
// or channelAccessToken.
export interface LineChannelRow {
  id: string;
  destination: string;
  companyId: string;
  active: boolean;
  createdAt?: string;
}

export interface LineAllowlistRow {
  id: string;
  lineUserId: string;
  companyId: string;
  active: boolean;
  note: string | null;
  createdAt?: string;
}

export interface LineQuarantineRow {
  id: string;
  fileName: string | null;
  reason: string | null;
  lineUserId: string | null;
  status: string;
}

const key = (companyId: string | undefined) => ['line-intake', companyId] as const;

export function useLineStatus(companyId: string | undefined) {
  return useQuery({
    queryKey: [...key(companyId), 'status'],
    queryFn: () => api<LineIntakeStatus>(`/companies/${companyId}/line-intake/status`),
    enabled: !!companyId,
  });
}

export function useLineChannels(companyId: string | undefined) {
  return useQuery({
    queryKey: [...key(companyId), 'channels'],
    queryFn: () => api<LineChannelRow[]>(`/companies/${companyId}/line-intake/channels`),
    enabled: !!companyId,
  });
}

export function useCreateLineChannel(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: LineChannelCreate) =>
      apiJson<LineChannelCreate, LineChannelRow>(
        'POST',
        `/companies/${companyId}/line-intake/channels`,
        dto,
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: key(companyId) }),
  });
}

export function useDeleteLineChannel(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiJson<undefined, void>('DELETE', `/companies/${companyId}/line-intake/channels/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: key(companyId) }),
  });
}

export function useLineAllowlist(companyId: string | undefined) {
  return useQuery({
    queryKey: [...key(companyId), 'allowlist'],
    queryFn: () => api<LineAllowlistRow[]>(`/companies/${companyId}/line-intake/allowlist`),
    enabled: !!companyId,
  });
}

export function useCreateLineAllowlistEntry(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: LineSenderAllowlistCreate) =>
      apiJson<LineSenderAllowlistCreate, LineAllowlistRow>(
        'POST',
        `/companies/${companyId}/line-intake/allowlist`,
        dto,
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: key(companyId) }),
  });
}

export function useDeleteLineAllowlistEntry(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiJson<undefined, void>('DELETE', `/companies/${companyId}/line-intake/allowlist/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: key(companyId) }),
  });
}

export function useLineQuarantine(companyId: string | undefined) {
  return useQuery({
    queryKey: [...key(companyId), 'quarantine'],
    queryFn: () => api<LineQuarantineRow[]>(`/companies/${companyId}/line-intake/quarantine`),
    enabled: !!companyId,
  });
}

export function useReleaseLineMessage(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiJson<undefined, void>(
        'POST',
        `/companies/${companyId}/line-intake/quarantine/${id}/release`,
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: key(companyId) }),
  });
}

export function useDiscardLineMessage(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiJson<undefined, void>(
        'POST',
        `/companies/${companyId}/line-intake/quarantine/${id}/discard`,
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: key(companyId) }),
  });
}
